import { Component, OnInit, ChangeDetectionStrategy, EventEmitter, ViewChild, Inject } from '@angular/core';
import { DataSourceConfig } from '@core/models';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ColumnSelectorDialogComponent } from '../column-selector-dialog/column-selector-dialog.component';

@Component({
  selector: 'app-list-item-dialog-selector',
  templateUrl: './list-item-selector-dialog.component.html',
  styleUrls: ['./list-item-selector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListItemSelectorDialogComponent implements OnInit {
  selectClicked: EventEmitter<any[]> = new EventEmitter();
  cancelClicked: EventEmitter<void> = new EventEmitter();
  addClicked: EventEmitter<void> = new EventEmitter();
  serverSideSearchTermChanged: EventEmitter<string> = new EventEmitter();
  dataSourceConfigChanged = new EventEmitter<DataSourceConfig>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  dataSource: MatTableDataSource<any>;
  selectedListItems = new SelectionModel<any>(this.data.allowMultiSelect, []);

  constructor(
    public dialogRef: MatDialogRef<ListItemSelectorDialogComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: {
      list: any[],
      dataSourceConfig: DataSourceConfig,
      isLoading: boolean,
      isMobileMode: boolean,
      title: string,
      icon: string,
      allowMultiSelect: boolean,
      showAddButton: boolean,
      showFilterButton: boolean,
      showListCount: boolean,
      useServerSideData: boolean
    }
  ) {
    this.dataSource = new MatTableDataSource(this.data.list);
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data: any, filter: string) => this.filterPredicate(data, filter);
    this.applyDataSource(this.data.list);
    this.applyDataSourceConfig(this.data.dataSourceConfig);
  }

  add() {
    this.addClicked.emit();
  }

  select() {
    this.selectClicked.emit(this.selectedListItems.selected);
    this.dialogRef.close();
  }

  cancel() {
    this.dialogRef.close();
  }

  page(pageEvent: PageEvent) {
    this.dataSourceConfigChanged.emit({ ...this.data.dataSourceConfig, pageIndex: pageEvent.pageIndex, pageSize: pageEvent.pageSize });
  }

  sortChange(sortEvent: Sort) {
    this.dataSourceConfigChanged.emit({ ...this.data.dataSourceConfig, sortByOrder: sortEvent.direction, sortBy: sortEvent.active });
  }

  selectItem(row: any) {
    this.selectedListItems.toggle(row);
  }

  masterToggle() {
    this.isAllSelected() ? this.selectedListItems.clear() : this.dataSource.data.forEach(row => this.selectedListItems.select(row));
  }

  isAllSelected() {
    const numSelected = this.selectedListItems.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  selectFilterColumns() {
    const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
      autoFocus: false,
      data: {
        columns: this.data.dataSourceConfig.filterColumns.map((column) => {
          return { name: column, selected: this.data.dataSourceConfig.selectedFilterColumns.includes(column) };
        })
      }
    });

    dialogRef.afterClosed().subscribe((selectedFilterColumns) => {
      if (selectedFilterColumns) {
        this.dataSourceConfigChanged.emit({ ...this.data.dataSourceConfig, selectedFilterColumns });
      }
    });
  }

  applyFilter(filterValue: string) {
    if (this.data.useServerSideData) { this.serverSideSearchTermChanged.emit(filterValue); }
    this.dataSource.paginator.firstPage();
    this.dataSourceConfigChanged.emit({ ...this.data.dataSourceConfig, filter: filterValue, pageIndex: 0 });
  }

  applyDataSource(list: any) {
    this.dataSource.data = list;
  }

  applyDataSourceConfig(dataSourceConfig: DataSourceConfig) {
    this.data.dataSourceConfig = dataSourceConfig;
    this.dataSource.filter = dataSourceConfig.filter;
    this.dataSource.paginator.pageIndex = dataSourceConfig.pageIndex;
    this.dataSource.paginator.pageSize = dataSourceConfig.pageSize;
    this.dataSource.paginator.pageSizeOptions = dataSourceConfig.pageSizeOptions;
    this.dataSource.sort.direction = dataSourceConfig.sortByOrder;
    this.dataSource.sort.active = dataSourceConfig.sortBy;
  }

  filterPredicate(data: any, filter: string): boolean {
    if (this.data.dataSourceConfig.selectedFilterColumns.length > 0) {
      return this.data.dataSourceConfig.selectedFilterColumns.length === 1
        ? data[this.data.dataSourceConfig.selectedFilterColumns[0]].toString().toLowerCase().includes(filter.trim().toLowerCase())
        : this.data.dataSourceConfig.selectedFilterColumns
          .reduce((prevVal, currVal, idx) => {
            return idx === 1 ? data[prevVal] + data[currVal] : prevVal + data[currVal];
          })
          .toString()
          .toLowerCase()
          .includes(filter.trim().toLowerCase());
    }
  }
}
